import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import {
  NamedLink,
  ProductCarouselSection,
  BrandsBanner,
  TrustBanner,
} from '../../components';
import BlogCard from '../../components/BlogCard/BlogCard';
import SkeepersCarouselReviews from '../../components/SkeepersCarouselReviews/SkeepersCarouselReviews';

import css from './LandingPage.module.css';

const ACCORDION_ICONS = {
  shield: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="20"
      height="20"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  bolt: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="20"
      height="20"
    >
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  ),
  home: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="20"
      height="20"
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
};

const useNavigateLink = () => {
  const history = useHistory();
  return useCallback(
    link => {
      if (!link || typeof link !== 'string') return;
      if (/^https?:\/\//i.test(link)) {
        window.location.href = link;
      } else {
        history.push(link);
      }
    },
    [history]
  );
};

export const HeroBlock = ({ block, isFirstHero = false }) => {
  const navigate = useNavigateLink();
  const slides = block.slides || [];

  return (
    <>
      <section className={css.hero}>
        <div className={css.container}>
          <div className={css.heroContent}>
            <div className={css.heroImageContainer}>
              <div className={css.heroSlideVisuals}>
                {slides.map((slide, idx) => {
                  const imageClickLink =
                    typeof slide.imageClickLink === 'string' ? slide.imageClickLink.trim() : '';
                  const isImageClickable = Boolean(imageClickLink);
                  const isLcp = isFirstHero && idx === 0;
                  const src = slide.imageUrl || slide.imageUrlSm || '';

                  const imgEl = src ? (
                    <img
                      src={src}
                      alt={slide.imageAlt || ''}
                      className={css.heroMobileCardImage}
                      loading={isLcp ? 'eager' : 'lazy'}
                      fetchpriority={isLcp ? 'high' : undefined}
                      decoding="async"
                      width="800"
                      height="500"
                    />
                  ) : (
                    <div className={css.heroMobileCardImage} aria-hidden="true" />
                  );

                  const HeadingTag = slide.isPrimaryHeading || (isLcp && idx === 0) ? 'h1' : 'h2';

                  return (
                    <div
                      key={slide.id || idx}
                      className={classNames(
                        css.heroMobileCard,
                        isImageClickable && css.heroMobileCardImageClickable
                      )}
                    >
                      {isImageClickable ? (
                        <button
                          type="button"
                          className={css.heroMobileImageLink}
                          onClick={() => navigate(imageClickLink)}
                          aria-label={slide.imageClickAriaLabel || slide.imageAlt || slide.title}
                        >
                          {imgEl}
                        </button>
                      ) : (
                        imgEl
                      )}
                      <div className={css.heroMobileOverlay}>
                        <HeadingTag
                          className={classNames(
                            css.heroMobileTitle,
                            HeadingTag !== 'h1' && css.heroMobileTitleSecondary
                          )}
                        >
                          {slide.title}
                        </HeadingTag>
                        {slide.subtitle ? (
                          <p className={css.heroMobileSubtitle}>{slide.subtitle}</p>
                        ) : null}
                        <div className={css.heroMobileChips}>
                          {(slide.ctas || []).map((cta, ctaIdx) => (
                            <button
                              key={`${slide.id}-cta-${ctaIdx}`}
                              type="button"
                              className={css.heroMobileChip}
                              onClick={() => navigate(cta.link)}
                            >
                              {cta.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className={css.section}>
        <div className={css.container} />
      </section>
    </>
  );
};

export const ProductCarouselBlock = ({ block, products = [], inProgress = false, config, intl }) => (
  <ProductCarouselSection
    products={products}
    inProgress={inProgress}
    error={null}
    title={block.title}
    subtitle={block.subtitle}
    categoryLink={block.categoryLink}
    viewAllLabel={block.viewAllLabel}
    config={config}
    intl={intl}
  />
);

export const AccordionBlock = ({ block }) => {
  const [openIndex, setOpenIndex] = useState(0);
  const items = block.items || [];

  return (
    <section className={css.smhSection}>
      <div className={css.smhContainer}>
        <div className={css.smhCard}>
          <div className={css.smhHeader}>
            <h2 className={css.smhTitle}>{block.title}</h2>
            {block.subtitle ? <p className={css.smhSubtitle}>{block.subtitle}</p> : null}
          </div>
          <div className={css.smhBody}>
            {block.imageUrl ? (
              <div className={css.smhImageWrap}>
                <img
                  src={block.imageUrl}
                  alt={block.imageAlt || ''}
                  className={css.smhImage}
                  loading="lazy"
                  decoding="async"
                  width="1200"
                  height="675"
                />
              </div>
            ) : null}
            <div className={css.smhBenefits}>
              {items.map((benefit, i) => {
                const isOpen = openIndex === i;
                return (
                  <div
                    key={i}
                    className={isOpen ? `${css.smhAccordion} ${css.smhAccordionOpen}` : css.smhAccordion}
                  >
                    <button
                      className={css.smhAccordionHeader}
                      onClick={() => setOpenIndex(isOpen ? -1 : i)}
                      aria-expanded={isOpen}
                      type="button"
                    >
                      <span className={css.smhAccordionTitle}>
                        {ACCORDION_ICONS[benefit.icon] || null} {benefit.title}
                      </span>
                      <span className={css.smhAccordionToggle} aria-hidden="true">
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    <div className={css.smhAccordionBody} aria-hidden={!isOpen}>
                      <p className={css.smhAccordionText}>{benefit.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const BrandsBannerBlock = () => <BrandsBanner />;

export const CategoriesBlock = ({ block }) => {
  const items = block.items || [];

  return (
    <section className={classNames(css.databaseFeaturedProducts, css.categoriesCarouselSection)}>
      <div className={css.container}>
        <div className={css.sectionHeader}>
          <div>
            <h2 className={css.sectionTitle}>Catégories Populaires</h2>
          </div>
        </div>
        <div className={css.carouselContainer}>
          <div className={css.productCarousel}>
            {items.map((category, index) => {
              const iconSrc = category.iconUrl || category.icon;
              const slugMatch = (category.url || '').match(/^\/products\/([^/?]+)/);
              const isSlugUrl = !!slugMatch;
              const slug = slugMatch ? slugMatch[1] : null;
              const queryMatch = category.url?.includes('?') ? category.url.split('?')[1] : '';
              const queryString = queryMatch ? `?${queryMatch}` : '';

              const linkProps = isSlugUrl
                ? {
                    name: 'SearchPageWithSlug',
                    params: { slug },
                    to: { search: queryString },
                  }
                : { name: 'SearchPage', to: { search: queryString || category.url?.replace('/products', '') || '' } };

              return (
                <NamedLink key={category.name || index} {...linkProps} className={css.carouselProductCard}>
                  <div className={css.brandLogoContainer}>
                    {iconSrc ? (
                      <>
                        <img
                          src={iconSrc}
                          alt={category.name}
                          className={css.categoryIconImage}
                          width="64"
                          height="64"
                          loading="lazy"
                          decoding="async"
                          onError={e => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) {
                              e.target.nextSibling.style.display = 'flex';
                            }
                          }}
                        />
                        <div className={css.brandLogoFallback} style={{ display: 'none' }}>
                          {category.name?.charAt(0)}
                        </div>
                      </>
                    ) : (
                      <div className={css.brandLogoFallback}>{category.name?.charAt(0)}</div>
                    )}
                  </div>
                  <h3 className={css.brandName}>{category.name}</h3>
                </NamedLink>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export const BlogGridBlock = ({ block, blogPosts = [] }) => {
  const count = Number(block.apiCount) || 4;
  const posts = (blogPosts || []).slice(0, count);
  const title = block.title || 'Les Derniers Articles de Notre Blog';

  return (
    <section className={css.blogSection}>
      <div className={css.container}>
        <h2 className={css.sectionTitle}>{title}</h2>
        <div className={css.blogGrid}>
          {posts.map((post, index) => (
            <div key={post?.id || post?.slug || index} className={css.blogGridCard}>
              {post ? <BlogCard post={post} /> : null}
            </div>
          ))}
        </div>
        <div className={css.blogCTA}>
          <NamedLink name="BlogListPage" className={css.blogCTAButton}>
            Lire Tous les Articles
          </NamedLink>
        </div>
      </div>
    </section>
  );
};

export const ReviewsBlock = ({ block }) => (
  <SkeepersCarouselReviews
    className={css.verifiedReviewsCarousel}
    titleClassName={css.sectionTitle}
    title={block.title}
    slidesCount={block.slidesCount}
  />
);

export const TrustBannerBlock = () => <TrustBanner />;

export const FeaturedProductBlock = ({ block, listing, config }) => {
  const navigate = useNavigateLink();
  if (!listing && !block.productId) return null;

  const title = block.title || listing?.attributes?.title || '';
  const description = block.description || listing?.attributes?.description || '';
  const image =
    listing?.images?.[0]?.attributes?.variants?.['listing-card']?.url ||
    listing?.images?.[0]?.attributes?.variants?.['scaled-medium']?.url ||
    listing?.images?.[0]?.attributes?.variants?.default?.url;
  const price = listing?.attributes?.price;
  const priceLabel =
    price && typeof price.amount === 'number'
      ? `${(price.amount / 100).toFixed(2).replace('.', ',')} ${price.currency}`
      : null;
  const listingId = listing?.id?.uuid || listing?.id;
  const titleForSlug = listing?.attributes?.title || 'produit';
  const slug = titleForSlug
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  const href = listingId ? `/product/${slug}/${listingId}` : '/products';
  const layoutRight = block.layout === 'imageRight';

  return (
    <section className={css.featuredProductSection}>
      <div
        className={classNames(css.featuredProductInner, layoutRight && css.featuredProductReverse)}
      >
        <div className={css.featuredProductMedia}>
          {image ? (
            <img src={image} alt={title} loading="lazy" decoding="async" width="600" height="600" />
          ) : null}
        </div>
        <div className={css.featuredProductCopy}>
          {block.eyebrow ? <p className={css.featuredProductEyebrow}>{block.eyebrow}</p> : null}
          <h2 className={css.featuredProductTitle}>{title}</h2>
          {priceLabel ? <p className={css.featuredProductPrice}>{priceLabel}</p> : null}
          {description ? (
            <p className={css.featuredProductDescription}>
              {String(description).slice(0, 320)}
              {String(description).length > 320 ? '…' : ''}
            </p>
          ) : null}
          <button
            type="button"
            className={css.featuredProductCta}
            onClick={() => navigate(href)}
          >
            {block.ctaLabel || 'Voir le produit'}
          </button>
        </div>
      </div>
    </section>
  );
};

export const ImageTextCtaBlock = ({ block }) => {
  const navigate = useNavigateLink();
  const layoutRight = block.layout === 'imageRight';
  const src = block.imageUrl || block.imageUrlSm;

  return (
    <section className={css.imageTextCtaSection}>
      <div className={classNames(css.imageTextCtaInner, layoutRight && css.imageTextCtaReverse)}>
        <div className={css.imageTextCtaMedia}>
          {src ? (
            <img
              src={src}
              alt={block.imageAlt || ''}
              loading="lazy"
              decoding="async"
              width="1200"
              height="800"
            />
          ) : null}
        </div>
        <div className={css.imageTextCtaCopy}>
          <h2 className={css.imageTextCtaTitle}>{block.title}</h2>
          {block.description ? <p className={css.imageTextCtaDescription}>{block.description}</p> : null}
          {block.ctaLink ? (
            <button
              type="button"
              className={css.imageTextCtaButton}
              onClick={() => navigate(block.ctaLink)}
            >
              {block.ctaLabel || 'Découvrir'}
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
};

/**
 * Shared landing block renderer (public page + admin preview).
 */
const LandingBlocks = ({
  blocks = [],
  blogPosts = [],
  productsByBlock = {},
  config,
  intl,
  preview = false,
}) => {
  let heroSeen = false;

  const nodes = [];
  const reviewsAndTrust = [];

  (blocks || []).forEach(block => {
    if (!block?.type) return;
    const key = block.id || block.type;

    switch (block.type) {
      case 'hero': {
        const isFirstHero = !heroSeen;
        heroSeen = true;
        nodes.push(<HeroBlock key={key} block={block} isFirstHero={isFirstHero} />);
        break;
      }
      case 'productCarousel':
        nodes.push(
          <ProductCarouselBlock
            key={key}
            block={block}
            products={productsByBlock[block.id] || []}
            inProgress={false}
            config={config}
            intl={intl}
          />
        );
        break;
      case 'accordion':
        nodes.push(<AccordionBlock key={key} block={block} />);
        break;
      case 'brandsBanner':
        nodes.push(<BrandsBannerBlock key={key} />);
        break;
      case 'categories':
        nodes.push(<CategoriesBlock key={key} block={block} />);
        break;
      case 'blogGrid':
        nodes.push(<BlogGridBlock key={key} block={block} blogPosts={blogPosts} />);
        break;
      case 'reviews':
        reviewsAndTrust.push(<ReviewsBlock key={key} block={block} />);
        break;
      case 'trustBanner':
        reviewsAndTrust.push(<TrustBannerBlock key={key} />);
        break;
      case 'featuredProduct':
        nodes.push(
          <FeaturedProductBlock
            key={key}
            block={block}
            listing={(productsByBlock[block.id] || [])[0]}
            config={config}
          />
        );
        break;
      case 'imageTextCta':
        nodes.push(<ImageTextCtaBlock key={key} block={block} />);
        break;
      default:
        break;
    }
  });

  if (reviewsAndTrust.length) {
    nodes.push(
      <div key="trust-wrap" className={css.trustBannerSpacer}>
        {reviewsAndTrust}
      </div>
    );
  }

  return (
    <div
      className={classNames(css.root, css.rootReady, preview && css.landingPreviewRoot)}
      data-landing-root
      data-ready="true"
      data-preview={preview ? 'true' : undefined}
    >
      {nodes}
    </div>
  );
};

export default LandingBlocks;
